import React, { useCallback, useEffect, useState, useMemo } from 'react';
import BlogWrapper from 'components/BlogWrapper';
import * as apis from 'apis';
import { useNavigate } from 'react-router-dom';
import useSeo from 'hooks/useSeo';

export default function BlogConainer() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const limit = 10;

  // 현재 선택된 카테고리 이름 찾기 (SEO용)
  const selectedCategoryName = useMemo(() => {
    if (!categoryId) return '';
    const category = categories.find(
      (c) => String(c.id) === String(categoryId),
    );
    return category ? category.name : '';
  }, [categoryId, categories]);

  // 동적 SEO 메타 태그 설정
  const metaTitle = selectedCategoryName
    ? `${selectedCategoryName} 시공 사례 | 빔몬스터`
    : '블로그 | 빔몬스터';

  const metaDesc = selectedCategoryName
    ? `빔몬스터의 ${selectedCategoryName} 관련 다양한 시공 사례와 정보를 확인해보세요.`
    : '빔프로젝터, LED 디스플레이 설치 사례 및 빔몬스터의 다양한 소식을 전해드립니다.';

  // Blog List SEO
  useSeo(
    metaTitle,
    metaDesc,
    // 'https://beam-monster-backend.s3.ap-northeast-2.amazonaws.com/2025-12-17/0728/3643.',
  );

  useEffect(() => {
    const fetchData = async () => {
      if (isLoading) return;
      setIsLoading(true);
      try {
        // API는 0부터 시작이면 변환
        const apiPage = Math.max(page - 1, 0);

        const res = await apis.getBlogList(
          apiPage,
          limit,
          search,
          categoryId ? Number(categoryId) : undefined,
        );

        console.log('fetchData ', res);
        // axios 응답: res.data
        const { data: list, total } = res.data;

        if (page === 1) {
          setData(list);
        } else {
          setData((prev) => [...prev, ...list]);
        }

        // Calculate if there are more items
        const loadedCount = (page - 1) * limit + list.length;
        setHasMore(loadedCount < total);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, categoryId]); // limit is constant

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await apis.getBlogCategories();
        setCategories(res.data);
      } catch (error) {
        console.error(error);
      }
    };
    fetchCategories();
  }, []);

  const handleSearch = useCallback((e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page
    setData([]); // Optional: clear data immediately to show loading or empty state
    setHasMore(true);
  }, []);

  const handleCategory = useCallback((e) => {
    setCategoryId(e.target.value);
    setPage(1);
    setData([]);
    setHasMore(true);
  }, []);

  const handleDetailMove = useCallback(
    (id, title) => {
      // SEO friendly slug: /blog/제목-형태
      const slug = title
        ? title
            .trim()
            .replace(/[^a-zA-Z0-9\u3131-\u3163\uac00-\ud7a3\s]/g, '')
            .replace(/\s+/g, '-')
        : '';
      navigate(`/blog/${slug}`);
    },
    [navigate],
  );

  const handleLoadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setPage((prev) => prev + 1);
    }
  }, [isLoading, hasMore]);

  return (
    <BlogWrapper
      data={data}
      search={search}
      categories={categories}
      handleSearch={handleSearch}
      handleCategory={handleCategory}
      handleDetailMove={handleDetailMove}
      handleLoadMore={handleLoadMore}
      hasMore={hasMore}
    />
  );
}
